/**
 * LoginPage Component
 * 
 * Beautiful SSO login page with Google and emailed one-time code authentication.
 * Matches the dark theme design of the YAPN dashboard.
 */

import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import yapnLogo from '../assets/yapn-logo.png';

// LoginPageContent - used when Auth0 is configured
function LoginPageContent() {
  const { loginWithRedirect, isLoading } = useAuth0();

  const handleGoogleLogin = () => {
    loginWithRedirect({
      authorizationParams: {
        connection: 'google-oauth2'
      }
    });
  };

  // Auth0 Passwordless Email connection: the user gets a one-time code by email
  const handleEmailLogin = () => {
    loginWithRedirect({
      authorizationParams: {
        connection: 'email'
      }
    });
  };

  return (
    <>
      <div className="space-y-4">
        <Button
          onClick={handleGoogleLogin}
          disabled={isLoading}
          className="w-full bg-white text-gray-900 hover:bg-gray-100 disabled:opacity-50 flex items-center justify-center gap-3"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {isLoading ? 'Signing in...' : 'Sign in with Google'}
        </Button>

        <Button
          onClick={handleEmailLogin}
          disabled={isLoading}
          className="w-full bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-50 flex items-center justify-center gap-3"
        >
          <Mail className="w-5 h-5" aria-hidden="true" />
          {isLoading ? 'Signing in...' : 'Sign in with email'}
        </Button>
        <p className="text-center text-gray-500 text-xs">We'll email you a one-time code.</p>
      </div>
    </>
  );
}

function LoginPage({ isAuth0Configured = true }) {

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700 p-8">
        <div className="text-center mb-8">
          <img src={yapnLogo} alt="YAPN" className="h-16 w-auto mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white mb-2">Welcome to YAPN</h1>
          <p className="text-gray-400">Sign in to access your restaurant dashboard</p>
        </div>

        {!isAuth0Configured && (
          <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
            <p className="text-yellow-400 text-sm font-semibold mb-2">⚠️ Configuration Required</p>
            <p className="text-yellow-300/80 text-xs mb-2">
              Auth0 environment variables are not configured. Please create a <code className="bg-gray-900 px-1.5 py-0.5 rounded text-yellow-200">.env</code> file with:
            </p>
            <div className="bg-gray-900 p-3 rounded text-xs font-mono text-yellow-200/90 space-y-1">
              <div>REACT_APP_AUTH0_DOMAIN=your-domain.auth0.com</div>
              <div>REACT_APP_AUTH0_CLIENT_ID=your-client-id</div>
            </div>
          </div>
        )}

        {isAuth0Configured ? (
          <LoginPageContent />
        ) : (
          <div className="space-y-4">
            <Button
              disabled={true}
              className="w-full bg-white text-gray-900 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </Button>

            <Button
              disabled={true}
              className="w-full bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              <Mail className="w-5 h-5" aria-hidden="true" />
              Sign in with email
            </Button>
          </div>
        )}

        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            Secure authentication powered by Auth0
          </p>
        </div>
      </Card>
    </div>
  );
}

export default LoginPage;
